import {
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
} from "react";
import { type AutocompleteCloseReason } from "@mui/material";

import { fieldDescribedBy } from "ui/forms/field-message/field-message.helpers";

import { getOptionLabelString, joinIds, splitAriaProps } from "ui/select/select.helpers";

import { useIsTruncated } from "hooks/use-is-truncated";

import type {
  FilterOptionOption,
  OptionType,
  SelectProps,
  SelectValue,
} from "ui/select/select.types";

type UseSelectProps<Option extends OptionType> = Pick<
  SelectProps<Option>,
  | "selectName"
  | "name"
  | "errMessage"
  | "helperText"
  | "disabled"
  | "startIcon"
  | "value"
  | "defaultValue"
  | "onChange"
  | "onInputChange"
  | "onOpen"
  | "onClose"
  | "getOptionDisabled"
  | "multiple"
  | "filterOption"
> & {
  renderSelectedValueAction?: (option: Option) => ReactNode;
  ref?: Ref<HTMLInputElement>;
};

type UseSelectOptions<Option extends OptionType, PassThrough extends object> =
  UseSelectProps<Option> & {
    /** The `Autocomplete` props `Select` does not own, `aria-*` among them. */
    passThroughProps?: PassThrough;
  };

/**
 * The state and wiring behind `Select`, kept out of its markup: the controlled / uncontrolled split, the
 * one `onChange` both modes go through, the open list, the clipped-value check and the handlers MUI's
 * `Autocomplete` is given.
 */
export const useSelect = <Option extends OptionType, PassThrough extends object = object>({
  passThroughProps = {} as PassThrough,
  selectName,
  name,
  errMessage,
  helperText,
  disabled = false,
  startIcon,
  value,
  defaultValue,
  onChange,
  onInputChange,
  onOpen,
  onClose,
  getOptionDisabled,
  multiple = false,
  filterOption,
  renderSelectedValueAction,
  ref,
}: UseSelectOptions<Option, PassThrough>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isValueClipped = useIsTruncated(inputRef);

  const isControlled = value !== undefined;
  const controlledValue: Option | Option[] | null = multiple
    ? ((value as Option[] | undefined) ?? [])
    : ((value as Option | null | undefined) ?? null);

  /**
   * Only a controlled single select knows its option here: an uncontrolled one keeps its value inside
   * `Autocomplete`. That is why its icon and value action are drawn from `value` alone.
   */
  const selectedOption = multiple || Array.isArray(controlledValue) ? null : controlledValue;
  const selectedLabel = selectedOption ? getOptionLabelString(selectedOption) : "";
  const selectedValueAction = selectedOption ? renderSelectedValueAction?.(selectedOption) : null;

  const valueProps = isControlled
    ? { value: controlledValue }
    : { defaultValue: (defaultValue as Option | Option[] | null | undefined) ?? undefined };

  /**
   * Destructured, the two branches' handlers merge into a union TypeScript will only call with
   * `Option & Option[]`. MUI hands back the shape `multiple` asked for — an array, `[]` once cleared, or
   * an option or `null` — which is the branch's own.
   */
  const handleChange = (_event: unknown, newValue: unknown) => {
    const emit = onChange as ((selected: SelectValue<Option>) => void) | undefined;

    emit?.(newValue as SelectValue<Option>);
  };

  const handleInputChange = (_event: unknown, inputValue: string) => {
    onInputChange?.(inputValue);
  };

  const handleMenuOpen = (event: SyntheticEvent) => {
    setIsMenuOpen(true);
    onOpen?.(event);
  };

  const handleMenuClose = (event: SyntheticEvent, reason: AutocompleteCloseReason) => {
    setIsMenuOpen(false);
    onClose?.(event, reason);
  };

  /** The input is both the clipping probe and the call site's `ref`, so one callback feeds the two. */
  const setInputRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;

    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      (ref as MutableRefObject<HTMLInputElement | null>).current = node;
    }
  };

  /** An option is disabled by its own `isDisabled`, or by the call site's `getOptionDisabled`. */
  const isOptionDisabled = (option: Option) =>
    !!option.isDisabled || (getOptionDisabled?.(option) ?? false);

  /** The legacy `filterOption(option, input)` contract, adapted to MUI's whole-list `filterOptions`. */
  const filterOptions = filterOption
    ? (opts: Option[], state: { inputValue: string }) =>
        opts.filter((option) => {
          const candidate: FilterOptionOption<Option> = {
            data: option,
            label: getOptionLabelString(option),
            value: String(option.value ?? ""),
          };

          return filterOption(candidate, state.inputValue);
        })
    : undefined;

  /** An error replaces the helper copy rather than stacking under it. */
  const hasError = !!errMessage;
  const showHelperText = !!helperText && !hasError;
  /** `aria-*` describe the `<input>`; on the `Autocomplete` root they would describe nothing. */
  const { ariaProps: inputAriaProps, otherProps: autocompleteProps } =
    splitAriaProps(passThroughProps);
  const ariaDescribedBy = joinIds(
    inputAriaProps["aria-describedby"],
    fieldDescribedBy(selectName, { errorMessage: errMessage, helperText }),
  );

  return {
    autocompleteProps,
    inputAriaProps,
    inputName: name ?? selectName,
    ariaDescribedBy,
    hasError,
    showHelperText,
    hasStartIcon: !!startIcon,
    // Figma draws a disabled select with nothing at its end, not a greyed-out chevron.
    forcePopupIcon: disabled ? (false as const) : ("auto" as const),
    isMenuOpen,
    /** The full value shows only when the control clips it, and never over the open list. */
    isValueTooltipSuppressed: !isValueClipped || isMenuOpen,
    selectedOption,
    selectedLabel,
    selectedValueAction,
    valueProps,
    handleChange,
    handleInputChange,
    handleMenuOpen,
    handleMenuClose,
    setInputRef,
    isOptionDisabled,
    filterOptions,
  };
};
